import { useEffect, useMemo, useRef, useState } from "react";
export type GeoPoint = Readonly<{
  latitude: number;
  longitude: number;
  altitudeM: number | null;
}>;
const TILE = 256,
  clamp = (v: number, min: number, max: number) =>
    Math.min(max, Math.max(min, v));
const lonX = (lon: number, z: number) => ((lon + 180) / 360) * 2 ** z;
const latY = (lat: number, z: number) => {
  const r = (clamp(lat, -85.051129, 85.051129) * Math.PI) / 180;
  return ((1 - Math.asinh(Math.tan(r)) / Math.PI) / 2) * 2 ** z;
};
const xLon = (x: number, z: number) => (x / 2 ** z) * 360 - 180;
const yLat = (y: number, z: number) =>
  (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / 2 ** z))) * 180) / Math.PI;
export function GeoPointMapDialog({
  open,
  value,
  onConfirm,
  onClose,
  fallbackCenter = { latitude: 42.5, longitude: 12.5 },
}: {
  open: boolean;
  value: GeoPoint | null;
  onConfirm: (point: GeoPoint | null) => void;
  onClose: () => void;
  fallbackCenter?: Pick<GeoPoint, "latitude" | "longitude">;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    drag = useRef<{ x: number; y: number; center: GeoPoint } | null>(null),
    [point, setPoint] = useState<GeoPoint | null>(value),
    [center, setCenter] = useState(
      value ?? { ...fallbackCenter, altitudeM: null },
    ),
    [zoom, setZoom] = useState(value ? 18 : 6),
    [message, setMessage] = useState(""),
    [mode, setMode] = useState<"pan" | "select">("pan"),
    [mapStyle, setMapStyle] = useState<"map" | "satellite">("map"),
    [offset, setOffset] = useState({ x: 0, y: 0 }),
    [query, setQuery] = useState(""),
    [results, setResults] = useState<
      { id: string; label: string; latitude: number; longitude: number }[]
    >([]),
    [searching, setSearching] = useState(false);
  useEffect(() => {
    const node = dialog.current;
    if (open && !node?.open) {
      setPoint(value);
      setCenter(value ?? { ...fallbackCenter, altitudeM: null });
      setZoom(value ? 18 : 6);
      node?.showModal();
    } else if (!open && node?.open) node.close();
  }, [open, value, fallbackCenter.latitude, fallbackCenter.longitude]);
  useEffect(() => {
    if (!open) return;
    const node = dialog.current,
      canvas = node?.querySelector<HTMLElement>(".geo-map-canvas"),
      wheel = (event: WheelEvent) => {
        event.preventDefault();
        setZoom((value) => clamp(value + (event.deltaY < 0 ? 1 : -1), 2, 19));
      };
    canvas?.setAttribute(
      "aria-label",
      "Mappa per la selezione della posizione. Usare la rotellina del mouse per lo zoom.",
    );
    canvas?.addEventListener("wheel", wheel, { passive: false });
    return () => {
      canvas?.removeEventListener("wheel", wheel);
    };
  }, [open]);
  const tiles = useMemo(() => {
    const cx = lonX(center.longitude, zoom),
      cy = latY(center.latitude, zoom),
      bx = Math.floor(cx) - 1,
      by = Math.floor(cy) - 1,
      n = 2 ** zoom;
    return Array.from({ length: 9 }, (_, i) => {
      const x = bx + (i % 3),
        y = by + Math.floor(i / 3),
        column = ((x % n) + n) % n,
        row = clamp(y, 0, n - 1);
      return {
        key: `${mapStyle}-${zoom}-${x}-${y}`,
        x,
        y,
        url:
          mapStyle === "satellite"
            ? `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${row}/${column}`
            : `https://tile.openstreetmap.org/${zoom}/${column}/${row}.png`,
      };
    });
  }, [center, zoom, mapStyle]);
  const bx = tiles[0]?.x ?? 0,
    by = tiles[0]?.y ?? 0,
    marker = point
      ? {
          left: (lonX(point.longitude, zoom) - bx) * TILE,
          top: (latY(point.latitude, zoom) - by) * TILE,
        }
      : null;
  const choose = (event: React.MouseEvent<HTMLDivElement>) => {
    if (mode !== "select") return;
    const box = event.currentTarget.getBoundingClientRect(),
      x = bx + ((event.clientX - box.left) / box.width) * 3,
      y = by + ((event.clientY - box.top) / box.height) * 2,
      next = {
        latitude: Number(yLat(y, zoom).toFixed(6)),
        longitude: Number(xLon(x, zoom).toFixed(6)),
        altitudeM: point?.altitudeM ?? null,
      };
    setPoint(next);
    setCenter(next);
    setMessage("Posizione selezionata. Confermare per riportarla nel form.");
  };
  const endPan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const dx = event.clientX - drag.current.x,
      dy = event.clientY - drag.current.y,
      start = drag.current.center,
      x = lonX(start.longitude, zoom) - dx / TILE,
      y = latY(start.latitude, zoom) - dy / TILE;
    setCenter({
      latitude: yLat(y, zoom),
      longitude: xLon(x, zoom),
      altitudeM: null,
    });
    drag.current = null;
    setOffset({ x: 0, y: 0 });
  };
  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (query.trim().length < 3) return;
    setSearching(true);
    setMessage("");
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&accept-language=it&q=${encodeURIComponent(query.trim())}`,
      );
      if (!response.ok) throw new Error();
      const data = (await response.json()) as {
        place_id: number;
        display_name: string;
        lat: string;
        lon: string;
      }[];
      setResults(
        data.map((item) => ({
          id: String(item.place_id),
          label: item.display_name,
          latitude: Number(item.lat),
          longitude: Number(item.lon),
        })),
      );
      if (!data.length) setMessage("Nessun luogo trovato.");
    } catch {
      setMessage("Ricerca non disponibile. Riprovare o usare le coordinate.");
    } finally {
      setSearching(false);
    }
  };
  const locate = () =>
    navigator.geolocation
      ? navigator.geolocation.getCurrentPosition(
          (p) => {
            const next = {
              latitude: Number(p.coords.latitude.toFixed(6)),
              longitude: Number(p.coords.longitude.toFixed(6)),
              altitudeM:
                p.coords.altitude === null
                  ? null
                  : Number(p.coords.altitude.toFixed(2)),
            };
            setPoint(next);
            setCenter(next);
            setZoom(18);
            setMessage(
              `Posizione acquisita; precisione stimata ${Math.round(p.coords.accuracy)} m.`,
            );
          },
          () =>
            setMessage(
              "Posizione non acquisita. Verificare il consenso del browser.",
            ),
          { enableHighAccuracy: true, timeout: 15000 },
        )
      : setMessage("Geolocalizzazione non disponibile nel dispositivo.");
  return (
    <dialog
      ref={dialog}
      className="geo-map-dialog"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <header>
        <div>
          <h2>Posizione geografica</h2>
          <p>
            Usa la mano per spostarti; passa a Seleziona punto per acquisire le
            coordinate.
          </p>
        </div>
        <button type="button" className="tool-button" onClick={onClose}>
          Chiudi
        </button>
      </header>
      <form className="geo-map-search" onSubmit={search}>
        <label>
          <span>Cerca luogo o indirizzo</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Comune, indirizzo o luogo"
          />
        </label>
        <button type="submit" disabled={searching || query.trim().length < 3}>
          {searching ? "Ricerca…" : "Cerca"}
        </button>
      </form>
      {results.length ? (
        <div
          className="geo-search-results"
          role="list"
          aria-label="Risultati ricerca"
        >
          {results.map((result) => (
            <button
              type="button"
              className="tool-button"
              role="listitem"
              key={result.id}
              onClick={() => {
                setCenter({ ...result, altitudeM: null });
                setZoom(17);
                setResults([]);
                setMessage(`Mappa centrata su: ${result.label}`);
              }}
            >
              {result.label}
            </button>
          ))}
        </div>
      ) : null}
      <div
        className="geo-map-toolbar"
        role="toolbar"
        aria-label="Comandi mappa"
      >
        <label className="geo-map-style">
          <span>Vista</span>
          <select
            value={mapStyle}
            onChange={(event) =>
              setMapStyle(event.target.value as "map" | "satellite")
            }
          >
            <option value="map">Mappa 2D</option>
            <option value="satellite">Satellite</option>
          </select>
        </label>
        <button
          type="button"
          className="tool-button"
          aria-pressed={mode === "pan"}
          onClick={() => setMode("pan")}
        >
          Mano
        </button>
        <button
          type="button"
          className="tool-button"
          aria-pressed={mode === "select"}
          onClick={() => setMode("select")}
        >
          Seleziona punto
        </button>
        <button type="button" className="tool-button" onClick={locate}>
          Usa posizione attuale
        </button>
        <button
          type="button"
          className="tool-button"
          onClick={() => setCenter({ ...fallbackCenter, altitudeM: null })}
        >
          Centra sulla posizione iniziale
        </button>
        {point ? (
          <button
            type="button"
            className="tool-button"
            onClick={() =>
              window.open(
                `https://www.google.com/maps?q=${point.latitude},${point.longitude}`,
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            Apri in Google Maps
          </button>
        ) : null}
      </div>
      <div
        className={`geo-map-canvas ${mode}`}
        role="application"
        aria-label="Mappa per la selezione della posizione"
        onClick={choose}
        onPointerDown={(event) => {
          if (mode !== "pan") return;
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = { x: event.clientX, y: event.clientY, center };
          setOffset({ x: 0, y: 0 });
        }}
        onPointerMove={(event) => {
          if (drag.current)
            setOffset({
              x: event.clientX - drag.current.x,
              y: event.clientY - drag.current.y,
            });
        }}
        onPointerUp={endPan}
        onPointerCancel={endPan}
      >
        <div
          className="geo-map-layer"
          style={{ transform: `translate(${offset.x}px,${offset.y}px)` }}
        >
          {tiles.map((tile) => (
            <img key={tile.key} src={tile.url} alt="" draggable={false} />
          ))}
          {marker ? (
            <span
              className="geo-map-marker"
              style={{ left: marker.left, top: marker.top }}
              aria-label="Posizione selezionata"
            />
          ) : null}
        </div>
        {mapStyle === "satellite" ? (
          <a
            className="geo-map-attribution"
            href="https://www.esri.com/en-us/legal/terms/full-master-agreement"
            target="_blank"
            rel="noreferrer"
          >
            Immagini © Esri e fornitori
          </a>
        ) : (
          <a
            className="geo-map-attribution"
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noreferrer"
          >
            © OpenStreetMap
          </a>
        )}
      </div>
      <div className="geo-coordinate-fields">
        <label>
          Latitudine
          <input
            type="number"
            min={-90}
            max={90}
            step="0.000001"
            value={point?.latitude ?? ""}
            onChange={(e) =>
              setPoint((c) => ({
                latitude: Number(e.target.value),
                longitude: c?.longitude ?? center.longitude,
                altitudeM: c?.altitudeM ?? null,
              }))
            }
          />
        </label>
        <label>
          Longitudine
          <input
            type="number"
            min={-180}
            max={180}
            step="0.000001"
            value={point?.longitude ?? ""}
            onChange={(e) =>
              setPoint((c) => ({
                latitude: c?.latitude ?? center.latitude,
                longitude: Number(e.target.value),
                altitudeM: c?.altitudeM ?? null,
              }))
            }
          />
        </label>
        <label>
          Altitudine (m)
          <input
            type="number"
            min={-500}
            max={10000}
            step="0.01"
            value={point?.altitudeM ?? ""}
            onChange={(e) =>
              setPoint((c) =>
                c
                  ? {
                      ...c,
                      altitudeM:
                        e.target.value === "" ? null : Number(e.target.value),
                    }
                  : null,
              )
            }
          />
        </label>
      </div>
      {message ? <p role="status">{message}</p> : null}
      <footer>
        <button
          type="button"
          className="tool-button"
          onClick={() => {
            setPoint(null);
            setMessage(
              "Selezione rimossa; il database cambierà solo dopo il salvataggio del form.",
            );
          }}
        >
          Rimuovi selezione
        </button>
        <span />
        <button type="button" className="tool-button" onClick={onClose}>
          Annulla
        </button>
        <button
          type="button"
          disabled={!point}
          onClick={() => onConfirm(point)}
        >
          Conferma posizione
        </button>
      </footer>
    </dialog>
  );
}
